import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Buildings, Plus, Trash, FileXls, X, Eye, ChartLine, Receipt, FileText, Clock, Camera, PencilSimple, ChatCircle, ArrowRight, FilePdf, CalendarBlank, PaperPlaneTilt } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearch } from '../components/VerticalTabs';
import { ProjectFormModal, ExpenseFormModal, PhotoUploadModal, PhotoPreviewModal } from '../components/projects/ProjectModals';
import { ProjectList, ProjectSummary, ExpensesTab, InvoicesTab, PhotosTab } from '../components/projects/ProjectDetailParts';
import { PhotosTabGrouped } from '../components/projects/PhotosTabGrouped';
import { ProjectQuotesPanel } from '../components/projects/ProjectQuotesPanel';
import { ProjectLaborMonthlyTab } from '../components/projects/ProjectLaborMonthlyTab';
import { ProjectsGanttView } from '../components/projects/ProjectsGanttView';
import { ReservationsTab } from '../components/projects/ReservationsTab';
import { ProjectDocumentsViewer } from '../components/projects/ProjectDocumentsViewer';
import { MaterialsListTab } from '../components/projects/MaterialsListTab';
import { OrdersTab } from '../components/site/OrdersTab';
import { BudgetTab } from '../components/projects/BudgetTab';
import { ProjectTabsBar } from '../components/projects/ProjectTabsBar';
import { LayoutToggle, useLayoutMode } from '../components/ui/LayoutToggle';
import { ProjectSubcontractorsTab } from '../components/projects/ProjectSubcontractorsTab';
import { ProjectContactsTab } from '../components/projects/ProjectContactsTab';
import { ProjectPurchasesTab } from '../components/projects/ProjectPurchasesTab';
import { ArchivedProjectsTab } from '../components/site/ArchivedProjectsTab';
import { Archive as ArchiveIcon, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { QuickInvoiceModal } from '../components/invoicing/QuickInvoiceModal';
import { QuoteSelectorModal } from '../components/invoicing/QuoteSelectorModal';
import { QuotePercentageDialog } from '../components/invoicing/QuotePercentageDialog';
import { WorksiteFeedTab } from '../components/projects/WorksiteFeedTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  { value: 'materials', label: 'Matériaux' },
  { value: 'labor', label: 'Main d\'œuvre' },
  { value: 'subcontractor', label: 'Sous-traitance' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'transport', label: 'Transport' },
  { value: 'other', label: 'Autre' }
];

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' },
  emise: { label: 'Émise', color: '#1565C0', bg: '#E3F2FD' },
  payee: { label: 'Payée', color: '#2E7D32', bg: '#E8F5E9' },
  annulee: { label: 'Annulée', color: '#C62828', bg: '#FFEBEE' }
};

const CONTRACT_TYPES = [
  { value: 'all', label: 'Tous', color: '#5E5F66' },
  { value: 'direct', label: 'Direct', color: '#1565C0' },
  { value: 'bon_commande', label: 'Bon de commande', color: '#F59E0B' },
  { value: 'sous_traitance', label: 'Sous-traitance', color: '#4F46E5' },
  { value: 'co_traitance', label: 'Co-traitance', color: '#0EA5E9' },
  { value: 'appel_offres', label: "Appel d'offres", color: '#7C3AED' },
];

const ProjectsGrid = ({ projects, onOpen, onUploadCover, onDeleteCover, canManage, layoutMode = 'grid' }) => {
  if (!projects.length) {
    return (
      <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}>
        <Buildings size={48} style={{ opacity: 0.4, marginBottom: '12px' }} />
        <p style={{ margin: 0, fontWeight: '500' }}>Aucun chantier en cours</p>
        <p style={{ fontSize: '0.85rem', marginTop: '4px' }}>Cliquez sur "Nouveau Chantier" pour en créer un.</p>
      </div>
    );
  }
  const isHorizontal = layoutMode === 'horizontal';
  const isVertical = layoutMode === 'vertical';
  const isList = layoutMode === 'list';
  const containerClass = isList ? 'layout-mode-list' : (isVertical ? 'layout-mode-vertical' : '');
  const containerStyle = (() => {
    if (isHorizontal) return { display: 'flex', gap: '16px', overflowX: 'auto', overflowY: 'visible', padding: '4px 4px 16px', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' };
    if (isVertical) return { display: 'flex', flexDirection: 'column', gap: '14px' };
    if (isList) return { display: 'flex', flexDirection: 'column', gap: '4px' };
    return { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' };
  })();

  // Compact list row rendering
  if (isList) {
    return (
      <div style={containerStyle} className={containerClass} data-testid={`projects-${layoutMode}`}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 130px 130px 110px 110px', gap: 8, padding: '8px 14px', background: '#F4F5F7', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, color: '#5E5F66', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          <div>Chantier / Client</div>
          <div style={{ textAlign: 'right' }}>Vente HT</div>
          <div style={{ textAlign: 'right' }}>Facture</div>
          <div style={{ textAlign: 'right' }}>Reste</div>
          <div style={{ textAlign: 'center' }}>Type</div>
          <div style={{ textAlign: 'center' }}>%</div>
        </div>
        {projects.map(p => {
          const sale = p.sale_price_ht || 0;
          const invoiced = p.total_invoiced_ht || 0;
          const pct = sale > 0 ? Math.min(100, Math.round((invoiced / sale) * 100)) : 0;
          const remaining = sale - invoiced;
          const ct = p.contract_type || 'direct';
          const ctMap = { direct: ['Direct', '#1565C0'], bon_commande: ['BdC', '#F59E0B'], sous_traitance: ['ST', '#4F46E5'], co_traitance: ['Co-T', '#0EA5E9'], appel_offres: ['AO', '#7C3AED'] };
          const [ctLabel, ctColor] = ctMap[ct] || ctMap.direct;
          return (
            <div key={p.id} className="card" data-testid={`project-card-${p.id}`} onClick={() => onOpen(p)}
              style={{ display: 'grid', gridTemplateColumns: '1fr 160px 130px 130px 110px 110px', gap: 8, padding: '10px 14px', alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 700, color: '#1A1A1E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  {p.margin_alert_active && (
                    <span data-testid={`margin-alert-badge-${p.id}`}
                      title={`Marge sous tension: coût ${p.margin_consumed_pct}% du prix de vente HT (seuil ${p.margin_alert_threshold_pct}%)`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.65rem', padding: '1px 6px', borderRadius: 10, background: '#C62828', color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      ⚠ Marge
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name || '-'}</div>
              </div>
              <div style={{ textAlign: 'right', fontWeight: 600 }}>{sale.toLocaleString('fr-FR')} EUR</div>
              <div style={{ textAlign: 'right', color: '#5E5F66' }}>{invoiced.toLocaleString('fr-FR')} EUR</div>
              <div style={{ textAlign: 'right', fontWeight: 700, color: remaining < 0 ? '#C62828' : '#2E7D32' }}>{remaining.toLocaleString('fr-FR')} EUR</div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: `${ctColor}15`, color: ctColor, fontSize: '0.72rem', fontWeight: 700 }}>{ctLabel}</span>
              </div>
              <div style={{ textAlign: 'center', fontWeight: 700, color: pct >= 100 ? '#2E7D32' : pct >= 50 ? '#1565C0' : '#5E5F66' }}>{pct}%</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={containerStyle} className={containerClass} data-testid={`projects-${layoutMode}`}>
      {projects.map(p => {
        const sale = p.sale_price_ht || 0;
        const invoiced = p.total_invoiced_ht || 0;
        const pct = sale > 0 ? Math.min(100, Math.round((invoiced / sale) * 100)) : 0;
        const remaining = sale - invoiced;
        return (
          <div key={p.id} className="card" data-testid={`project-card-${p.id}`}
               onClick={() => onOpen(p)}
               style={{ overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', ...(isHorizontal ? { flex: '0 0 320px', scrollSnapAlign: 'start' } : {}) }}
               onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.08)'; }}
               onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}>
            {p.cover_url ? (
              <div className="inline-soft-bg" style={{ position: 'relative', width: '100%', height: '160px' }}>
                <img
                  src={`${API_URL}${p.cover_url}?v=${p.id}`}
                  alt={p.name}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {canManage && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteCover(p.id); }}
                    data-testid={`proj-cover-delete-${p.id}`}
                    style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    title="Supprimer la photo"
                  ><Trash size={14} /></button>
                )}
              </div>
            ) : canManage ? (
              <label
                onClick={(e) => e.stopPropagation()}
                data-testid={`proj-cover-add-${p.id}`}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '80px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', color: '#5E5F66', cursor: 'pointer', fontSize: '0.82rem', gap: '6px', borderBottom: '1px solid #E2E2E6' }}
              >
                <Camera size={18} /> Ajouter une photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadCover(p.id, f); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
              </label>
            ) : null}
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #F4F5F7', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '42px', height: '42px', borderRadius: '8px', backgroundColor: '#E3F2FD', color: '#002FA7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Buildings size={22} weight="bold" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: '700', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>{p.name}</div>
                  {p.autoliquidation_tva && (
                    <span data-testid={`autoliq-badge-${p.id}`} style={{
                      display: 'inline-flex', alignItems: 'center',
                      padding: '2px 8px',
                      backgroundColor: '#C62828',
                      color: 'white',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      borderRadius: '999px',
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(198, 40, 40, 0.3)',
                    }} title="TVA 0% AUTOLIQUIDATION">AUTOLIQ</span>
                  )}
                  {p.margin_alert_active && (
                    <span data-testid={`margin-alert-badge-${p.id}`}
                      title={`Marge sous tension: coût ${p.margin_consumed_pct}% du prix de vente HT (seuil ${p.margin_alert_threshold_pct}%)`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '2px 8px',
                        backgroundColor: '#C62828', color: 'white',
                        fontSize: '0.65rem', fontWeight: 800,
                        borderRadius: 999,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(198, 40, 40, 0.35)',
                      }}>⚠ Marge tension</span>
                  )}
                  {/* Marge prevue badge */}
                  {(() => {
                    if (!sale || sale === 0) return null;
                    const cats = p.expenses_by_category || {};
                    const totalCosts = Object.values(cats).reduce((s, v) => s + (v || 0), 0) + (p.labor_cost_ht || 0);
                    const margin = sale - totalCosts;
                    const marginPct = (margin / sale) * 100;
                    const color = marginPct < 0 ? '#C62828' : marginPct < 15 ? '#E65100' : '#2E7D32';
                    const bg = marginPct < 0 ? '#FFEBEE' : marginPct < 15 ? '#FFF3E0' : '#E8F5E9';
                    const sign = margin >= 0 ? '+' : '';
                    return (
                      <span data-testid={`margin-badge-${p.id}`} title={`Marge prevue: ${margin.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} EUR sur ${sale.toLocaleString('fr-FR')} EUR de vente`}
                        style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '2px 8px',
                          backgroundColor: bg, color,
                          fontSize: '0.7rem', fontWeight: 800,
                          borderRadius: 999, border: `1px solid ${color}33`,
                          whiteSpace: 'nowrap',
                        }}>
                        Marge {sign}{marginPct.toFixed(0)}%
                      </span>
                    );
                  })()}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#5E5F66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.client_name || '-'}</div>
              </div>
            </div>
            <div style={{ padding: '14px 18px', fontSize: '0.85rem', display: 'grid', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#5E5F66' }}>
                <span>Vente HT</span>
                <strong style={{ color: '#1A1A1E' }}>{sale.toLocaleString('fr-FR')} EUR</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#5E5F66' }}>
                <span>Reste à facturer</span>
                <strong style={{ color: remaining < 0 ? '#C62828' : '#2E7D32' }}>{remaining.toLocaleString('fr-FR')} EUR</strong>
              </div>

              {/* Depenses realisees: achats / carburant / main d'oeuvre — toujours affichees (meme a 0) */}
              {(() => {
                const cats = p.expenses_by_category || {};
                const achats = (cats.materials || 0) + (cats.subcontractor || 0) + (cats.rental || 0) + (cats.logistics || 0) + (cats.other || 0);
                const carburant = cats.fuel || 0;
                const mo = p.labor_cost_ht || 0;
                return (
                  <div data-testid={`card-expenses-${p.id}`} style={{ marginTop: '4px', padding: '8px 10px', borderRadius: 6, backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB', display: 'grid', gap: 4 }}>
                    <div style={{ fontSize: '0.7rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>Depenses realisees</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                      <span style={{ color: '#5E5F66' }}>Achats</span>
                      <strong style={{ color: achats > 0 ? '#1565C0' : '#9CA3AF' }}>{achats.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} EUR</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                      <span style={{ color: '#5E5F66' }}>Carburant</span>
                      <strong style={{ color: carburant > 0 ? '#EF6C00' : '#9CA3AF' }}>{carburant.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} EUR</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                      <span style={{ color: '#5E5F66' }}>Main d'oeuvre{p.total_hours ? ` (${p.total_hours}h)` : ''}</span>
                      <strong style={{ color: mo > 0 ? '#C62828' : '#9CA3AF' }}>{mo.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} EUR</strong>
                    </div>
                  </div>
                );
              })()}

              <div style={{ marginTop: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#5E5F66', marginBottom: '3px' }}>
                  <span>Avancement facturation</span>
                  <strong style={{ color: pct >= 100 ? '#2E7D32' : (pct > 0 ? '#E65100' : '#C62828') }}>{pct}%</strong>
                </div>
                <div className="inline-track" style={{ height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: pct >= 100 ? '#2E7D32' : '#E65100' }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProjectsPage = ({ embedded }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [linkedEmployeeExpenses, setLinkedEmployeeExpenses] = useState([]);
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [projectQuotes, setProjectQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [activeTab, setActiveTab] = useState('gestion');
  const [subTab, setSubTab] = useState('en_cours');
  const [photos, setPhotos] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoComment, setPhotoComment] = useState('');
  const [photoFolder, setPhotoFolder] = useState('');
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [editPhotoComment, setEditPhotoComment] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [quickInvoice, setQuickInvoice] = useState({ show: false, type: 'facture' });
  const [showProjectQuoteSelector, setShowProjectQuoteSelector] = useState(false);
  const [convertDialog, setConvertDialog] = useState({ open: false, quote: null, submitting: false, previousAdvancements: {} });
  const [contractFilter, setContractFilter] = useState('all');
  const [layoutMode, setLayoutMode] = useLayoutMode('projects', 'horizontal');

  const handleCreateInvoiceFlowProject = () => setShowProjectQuoteSelector(true);
  const handlePickQuoteForProject = async (quote) => {
    setShowProjectQuoteSelector(false);
    let prev = {};
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/quotes/${quote.id}/advancements`, { withCredentials: true });
      prev = r.data?.previous_advancements || {};
    } catch { /* ignore */ }
    setConvertDialog({ open: true, quote, submitting: false, previousAdvancements: prev });
  };
  const handleCreateNewQuoteFromProject = () => {
    setShowProjectQuoteSelector(false);
    setQuickInvoice({ show: true, type: 'devis' });
    toast.info('Creez d\'abord le devis. Vous pourrez ensuite le facturer.');
  };
  const handleConvertConfirmProject = async (lineAdvancements) => {
    const q = convertDialog.quote;
    if (!q) return;
    setConvertDialog(d => ({ ...d, submitting: true }));
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/quotes/${q.id}/convert`, { line_advancements: lineAdvancements }, { withCredentials: true });
      toast.success(`Facture ${res.data.number} creee depuis le devis`);
      setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} });
      if (selectedProject?.id) fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la conversion');
      setConvertDialog(d => ({ ...d, submitting: false }));
    }
  };
  const [projectForm, setProjectForm] = useState({
    name: '', client_name: '', address: '', client_id: '', sale_price_ht: 0, tva_rate: 20, hourly_rate: 0, start_date: '', end_date: '', autoliquidation_tva: false,
    guarantee_type: null, retention_percent: null, retention_release_date: '', caution_attestation_doc_id: '',
    prorata_enabled: false, prorata_percent: 0, prorata_amount: 0,
    contract_type: 'direct', purchase_order_number: '', purchase_order_doc_id: '', mandataire_company: ''
  });
  const [expenseForm, setExpenseForm] = useState({
    category: 'materials', description: '', amount_ht: 0, tva_rate: 20, date: format(new Date(), 'yyyy-MM-dd'), hours: ''
  });

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchProjectDetails = useCallback(async (projectId) => {
    try {
      const [expRes, invRes, photosRes, quotesRes, empExpRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${projectId}/expenses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/by-project/${projectId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${projectId}/photos`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/quotes`, { params: { project_id: projectId }, withCredentials: true }),
        axios.get(`${API_URL}/api/employee-expenses`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setExpenses(expRes.data);
      setBillingInvoices(invRes.data);
      setPhotos(photosRes.data);
      setProjectQuotes(quotesRes.data || []);
      // Only keep employee expenses linked to this chantier
      setLinkedEmployeeExpenses((empExpRes.data || []).filter((e) => e.project_id === projectId));
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  }, [API_URL]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Auto-open a project when ?openProject=<id> is present in the URL.
  // Used by dashboard widgets (Pret a facturer, Rentabilite) to deep-link to
  // a specific project.
  const location = useLocation();
  useEffect(() => {
    if (!projects.length) return;
    const params = new URLSearchParams(location.search);
    const wantedId = params.get('openProject');
    if (!wantedId) return;
    const proj = projects.find((p) => p.id === wantedId);
    if (proj && (!selectedProject || selectedProject.id !== wantedId)) {
      setSelectedProject(proj);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.search]);

  const handleCoverUpload = async (projectId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Photo trop lourde (max 10 Mo)'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/projects/${projectId}/cover`, fd, { withCredentials: true });
      toast.success('Photo ajoutee');
      fetchProjects();
      if (selectedProject?.id === projectId) {
        const res = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
        const fresh = (res.data || []).find(p => p.id === projectId);
        if (fresh) setSelectedProject(fresh);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur upload photo'); }
  };

  const handleCoverDelete = async (projectId) => {
    if (!window.confirm('Supprimer la photo du chantier ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}/cover`, { withCredentials: true });
      toast.success('Photo supprimee');
      fetchProjects();
      if (selectedProject?.id === projectId) {
        setSelectedProject({ ...selectedProject, cover_url: '' });
      }
    } catch (e) { toast.error('Erreur'); }
  };

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetails(selectedProject.id);
    }
  }, [selectedProject, fetchProjectDetails]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      if (editProject) {
        await axios.put(`${API_URL}/api/projects/${editProject.id}`, projectForm, { withCredentials: true });
        toast.success('Chantier modifié');
      } else {
        await axios.post(`${API_URL}/api/projects`, projectForm, { withCredentials: true });
        toast.success('Chantier créé');
      }
      setShowProjectModal(false);
      setEditProject(null);
      setProjectForm({ name: '', client_name: '', address: '', client_id: '', sale_price_ht: 0, tva_rate: 20, hourly_rate: 0, start_date: '', end_date: '', autoliquidation_tva: false, guarantee_type: null, retention_percent: null, retention_release_date: '', caution_attestation_doc_id: '', prorata_enabled: false, prorata_percent: 0, prorata_amount: 0, contract_type: 'direct', purchase_order_number: '', purchase_order_doc_id: '', mandataire_company: '' });
      fetchProjects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/projects/${selectedProject.id}/expenses`, {
        ...expenseForm,
        project_id: selectedProject.id,
        hours: expenseForm.hours ? parseFloat(expenseForm.hours) : null
      }, { withCredentials: true });
      toast.success('Dépense ajoutée');
      setShowExpenseModal(false);
      setExpenseForm({ category: 'materials', description: '', amount_ht: 0, tva_rate: 20, date: format(new Date(), 'yyyy-MM-dd'), hours: '' });
      fetchProjectDetails(selectedProject.id);
      fetchProjects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Supprimer ce chantier et toutes ses données ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${id}`, { withCredentials: true });
      toast.success('Chantier supprimé');
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API_URL}/api/projects/${selectedProject.id}/expenses/${expenseId}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      fetchProjectDetails(selectedProject.id);
      fetchProjects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    const files = Array.isArray(photoFile) ? photoFile : (photoFile ? [photoFile] : []);
    if (files.length === 0) return toast.error('Selectionnez au moins une photo');
    try {
      if (files.length === 1 && !photoFolder.trim()) {
        const fd = new FormData();
        fd.append('file', files[0]);
        fd.append('comment', photoComment);
        await axios.post(`${API_URL}/api/projects/${selectedProject.id}/photos`, fd, { withCredentials: true });
      } else {
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        fd.append('comment', photoComment);
        if (photoFolder.trim()) fd.append('folder', photoFolder.trim());
        await axios.post(`${API_URL}/api/projects/${selectedProject.id}/photos/batch`, fd, { withCredentials: true });
      }
      toast.success(`${files.length} photo(s) ajoutee(s)${photoFolder.trim() ? ` dans « ${photoFolder.trim()} »` : ''}`);
      setShowPhotoModal(false);
      setPhotoFile(null);
      setPhotoComment('');
      setPhotoFolder('');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors de l'upload");
    }
  };

  const handleUpdatePhotoComment = async (photoId) => {
    try {
      await axios.put(`${API_URL}/api/projects/${selectedProject.id}/photos/${photoId}`, { comment: editPhotoComment }, { withCredentials: true });
      toast.success('Commentaire mis à jour');
      setEditingPhotoId(null);
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${selectedProject.id}/photos/${photoId}`, { withCredentials: true });
      toast.success('Photo supprimée');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const exportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/export/excel`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gestion_chantiers.xlsx';
      link.click();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur export');
    }
  };

  const handleArchiveProject = async () => {
    if (!selectedProject) return;
    if (!window.confirm(`Archiver le chantier "${selectedProject.name}" ? Il ne sera plus visible pour les salaries.`)) return;
    try {
      await axios.put(`${API_URL}/api/projects/${selectedProject.id}/archive`, {}, { withCredentials: true });
      toast.success('Chantier archive');
      setSelectedProject(null);
      fetchProjects();
    } catch (e) { toast.error('Erreur archivage'); }
  };

  const handleGenerateDGD = async (projectId) => {
    if (!window.confirm("Generer le Decompte General Definitif (DGD) ?\nUn PDF et un Excel seront crees dans l'onglet Documents du chantier, et une facture de solde sera generee automatiquement (statut brouillon).")) return;
    try {
      const res = await axios.post(`${API_URL}/api/projects/${projectId}/dgd/generate`, {}, { withCredentials: true });
      const inv = res.data?.settlement_invoice;
      const summary = res.data?.summary || {};
      const restePart = `Reste a facturer : ${(summary.reste_a_facturer || 0).toLocaleString('fr-FR')} EUR HT`;
      const invPart = inv ? ` · Facture de solde ${inv.number} (brouillon)` : '';
      toast.success(`DGD genere - ${restePart}${invPart}`);
      try { fetchProjectDetails(projectId); } catch {}
      // Ask if user wants to send to client right away
      if (inv && window.confirm(`Voulez-vous envoyer immediatement le DGD + la facture de solde ${inv.number} au client par email ?`)) {
        handleSendDGD(projectId);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Echec generation DGD');
    }
  };

  const handleSendDGD = async (projectId) => {
    try {
      const res = await axios.post(`${API_URL}/api/projects/${projectId}/dgd/send-to-client`, {}, { withCredentials: true });
      const dgdNote = res.data?.dgd_attached ? '' : ' (DGD PDF introuvable, seule la facture a ete envoyee)';
      toast.success(`Envoye a ${res.data?.to} - Facture ${res.data?.invoice_number} marquee emise${dgdNote}`);
      try { fetchProjectDetails(projectId); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Echec d'envoi");
    }
  };

  const getCategoryLabel = (value) => EXPENSE_CATEGORIES.find(c => c.value === value)?.label || value;

  const { searchTerm } = useSearch();
  const filteredProjects = projects.filter(p => {
    if (contractFilter !== 'all' && (p.contract_type || 'direct') !== contractFilter) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (p.name || '').toLowerCase().includes(s) || (p.address || '').toLowerCase().includes(s) || (p.client_name || '').toLowerCase().includes(s);
  });

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="projects-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Gestion de Chantiers</h1>
          <p className="page-subtitle">Suivi des operations et depenses</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={exportExcel}>
            <FileXls size={18} /> Export Excel
          </button>
          <button data-testid="add-project-btn" className="btn btn-primary" onClick={() => {
            setEditProject(null);
            setProjectForm({ name: '', client_name: '', address: '', client_id: '', sale_price_ht: 0, tva_rate: 20, hourly_rate: 0, start_date: '', end_date: '', autoliquidation_tva: false, guarantee_type: null, retention_percent: null, retention_release_date: '', caution_attestation_doc_id: '', prorata_enabled: false, prorata_percent: 0, prorata_amount: 0, contract_type: 'direct', purchase_order_number: '', purchase_order_doc_id: '', mandataire_company: '' });
            setShowProjectModal(true);
          }}>
            <Plus size={18} /> Nouveau Chantier
          </button>
        </div>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '16px' }}>
          <button className="btn btn-secondary" onClick={exportExcel}>
            <FileXls size={18} /> Export Excel
          </button>
          <button data-testid="add-project-btn" className="btn btn-primary" onClick={() => {
            setEditProject(null);
            setProjectForm({ name: '', client_name: '', address: '', client_id: '', sale_price_ht: 0, tva_rate: 20, hourly_rate: 0, start_date: '', end_date: '', autoliquidation_tva: false, guarantee_type: null, retention_percent: null, retention_release_date: '', caution_attestation_doc_id: '', prorata_enabled: false, prorata_percent: 0, prorata_amount: 0, contract_type: 'direct', purchase_order_number: '', purchase_order_doc_id: '', mandataire_company: '' });
            setShowProjectModal(true);
          }}>
            <Plus size={18} /> Nouveau Chantier
          </button>
        </div>
      )}

      {/* Sub-tabs En cours / Planning / Archives */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '20px' }}>
        <button data-testid="subtab-en-cours" onClick={() => { setSubTab('en_cours'); setSelectedProject(null); }}
          style={{ padding: '10px 24px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem',
                   fontWeight: subTab === 'en_cours' ? '700' : '500',
                   borderBottom: subTab === 'en_cours' ? '2px solid #002FA7' : '2px solid transparent',
                   marginBottom: '-2px', color: subTab === 'en_cours' ? '#002FA7' : '#5E5F66',
                   display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Buildings size={16} /> Chantiers en cours
        </button>
        <button data-testid="subtab-planning" onClick={() => { setSubTab('planning'); setSelectedProject(null); }}
          style={{ padding: '10px 24px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem',
                   fontWeight: subTab === 'planning' ? '700' : '500',
                   borderBottom: subTab === 'planning' ? '2px solid #002FA7' : '2px solid transparent',
                   marginBottom: '-2px', color: subTab === 'planning' ? '#002FA7' : '#5E5F66',
                   display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CalendarBlank size={16} /> Planning Gantt
        </button>
        <button data-testid="subtab-archives" onClick={() => { setSubTab('archives'); setSelectedProject(null); }}
          style={{ padding: '10px 24px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem',
                   fontWeight: subTab === 'archives' ? '700' : '500',
                   borderBottom: subTab === 'archives' ? '2px solid #002FA7' : '2px solid transparent',
                   marginBottom: '-2px', color: subTab === 'archives' ? '#002FA7' : '#5E5F66',
                   display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ArchiveIcon size={16} /> Chantiers archivés
        </button>
      </div>

      {subTab === 'planning' ? (
        <ProjectsGanttView projects={projects} onProjectChanged={fetchProjects} />
      ) : subTab === 'archives' ? (
        selectedProject ? (
          <div>
            <button className="btn btn-secondary" onClick={() => setSelectedProject(null)} style={{ marginBottom: '16px' }} data-testid="back-to-archives-btn">
              ← Retour aux chantiers archivés
            </button>
            <div style={{ padding: '10px 14px', marginBottom: '16px', background: '#FFF3E0', border: '1px solid #FFB74D', borderRadius: '6px', fontSize: '0.85rem', color: '#E65100' }}>
              <strong>Consultation d'un chantier archivé</strong> - Restaurez-le pour le modifier.
            </div>
            <ProjectSummary project={selectedProject} employeeExpenses={linkedEmployeeExpenses} onReload={async () => {
              const r = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
              setProjects(r.data);
              const fresh = (r.data || []).find((p) => p.id === selectedProject.id);
              if (fresh) setSelectedProject(fresh);
              fetchProjectDetails(selectedProject.id);
            }} />
            <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '20px', overflowX: 'auto' }}>
              {[
                { key: 'feed', label: 'Fil d\'avancement' },
                { key: 'gestion', label: 'Gestion' },
                { key: 'invoices', label: `Situations (${billingInvoices.length})` },
                { key: 'photos', label: `Photos (${photos.length})` },
                { key: 'documents', label: 'Documents' },
                { key: 'courses', label: 'Liste de courses' },
                { key: 'reservations', label: 'SAV / Reserves' }
              ].map((tab) => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`project-tab-${tab.key}`}
                  style={{ padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: '0.88rem', fontWeight: '500', backgroundColor: 'transparent', borderBottom: activeTab === tab.key ? '2px solid #002FA7' : '2px solid transparent', color: activeTab === tab.key ? '#002FA7' : '#5E5F66', marginBottom: '-2px', whiteSpace: 'nowrap' }}>
                  {tab.label}
                </button>
              ))}
            </div>
            {activeTab === 'feed' && <WorksiteFeedTab project={selectedProject} currentUser={currentUser} />}
            {activeTab === 'invoices' && <InvoicesTab invoices={billingInvoices} navigate={navigate} />}
            {activeTab === 'photos' && <PhotosTabGrouped photos={photos} project={selectedProject} onUpload={() => toast.info('Chantier archive - restaurez-le pour modifier')} onUpdateComment={() => {}} onDelete={() => {}} editingPhotoId={null} setEditingPhotoId={() => {}} editPhotoComment={''} setEditPhotoComment={() => {}} setPreviewPhoto={setPreviewPhoto} />}
            {activeTab === 'documents' && <ProjectDocumentsViewer project={selectedProject} canManage={false} />}
            {activeTab === 'courses' && <MaterialsListTab project={selectedProject} isAdmin={true} />}
            {activeTab === 'gestion' && <BudgetTab project={selectedProject} />}
            {activeTab === 'reservations' && <ReservationsTab project={selectedProject} currentUser={currentUser} />}
          </div>
        ) : (
          <ArchivedProjectsTab onOpen={(p) => { setSelectedProject(p); setActiveTab('gestion'); }} />
        )
      ) : selectedProject ? (
        <div>
          <button className="btn btn-secondary" onClick={() => setSelectedProject(null)} style={{ marginBottom: '16px' }} data-testid="back-to-list-btn">
            ← Retour à la liste
          </button>
          <div style={{ marginBottom: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '14px' }} data-testid="project-header-title-block">
            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 800, color: '#002FA7', letterSpacing: '-0.5px' }} data-testid="project-header-title">{selectedProject.name}</h1>
            {selectedProject.client_name && <span style={{ fontSize: '1rem', color: '#5E5F66' }}>· {selectedProject.client_name}</span>}
            {selectedProject.address && <span style={{ fontSize: '0.85rem', color: '#9E9EA7' }}>{selectedProject.address}</span>}
            {/* Red banners for missing actions */}
            {selectedProject.guarantee_type === 'caution' && !selectedProject.caution_attestation_doc_id && (
              <span data-testid="caution-missing-banner" style={{ fontSize: '0.95rem', fontWeight: 800, color: '#C62828', background: '#FEE2E2', padding: '4px 10px', borderRadius: 6, border: '1px solid #C62828' }}>
                (a ajouter l'attestation de caution)
              </span>
            )}
            {selectedProject.contract_type === 'bon_commande' && !selectedProject.purchase_order_number && (
              <span data-testid="po-missing-banner" style={{ fontSize: '1.05rem', fontWeight: 900, color: '#FFFFFF', background: '#C62828', padding: '4px 12px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                N° BON DE COMMANDE A RENSEIGNER
              </span>
            )}
            {selectedProject.contract_type === 'sous_traitance' && selectedProject.mandataire_company && (
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#4F46E5', background: '#EEF2FF', padding: '4px 10px', borderRadius: 6, border: '1px solid #4F46E5' }}>
                Mandataire : {selectedProject.mandataire_company}
              </span>
            )}
          </div>
          {selectedProject.cover_url ? (
            <div className="inline-soft-bg" style={{ position: 'relative', width: '100%', maxHeight: '260px', overflow: 'hidden', borderRadius: '10px', marginBottom: '20px' }}>
              <img
                src={`${API_URL}${selectedProject.cover_url}?v=${selectedProject.id}`}
                alt={selectedProject.name}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ width: '100%', maxHeight: '260px', objectFit: 'cover', display: 'block' }}
              />
              <button
                type="button"
                onClick={() => handleCoverDelete(selectedProject.id)}
                data-testid={`proj-cover-delete-detail-${selectedProject.id}`}
                style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Supprimer la photo"
              ><Trash size={16} /></button>
            </div>
          ) : (
            <label data-testid={`proj-cover-add-detail-${selectedProject.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '80px', background: 'linear-gradient(135deg, #EEF1FC, #E2E8F7)', color: '#5E5F66', cursor: 'pointer', fontSize: '0.9rem', gap: '8px', border: '1px dashed #C7D0E8', borderRadius: '10px', marginBottom: '20px' }}>
              <Camera size={20} /> Ajouter une photo de chantier
              <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCoverUpload(selectedProject.id, f); e.target.value = ''; }} style={{ display: 'none' }} />
            </label>
          )}
          <ProjectSummary project={selectedProject} employeeExpenses={linkedEmployeeExpenses} onReload={async () => {
            const r = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
            setProjects(r.data);
            const fresh = (r.data || []).find((p) => p.id === selectedProject.id);
            if (fresh) setSelectedProject(fresh);
            fetchProjectDetails(selectedProject.id);
          }} />

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <button className="btn btn-secondary" onClick={() => setQuickInvoice({ show: true, type: 'devis' })} data-testid="create-quote-from-project">
                  <FileText size={16} /> Creer un devis
                </button>
                <button className="btn btn-secondary" onClick={handleCreateInvoiceFlowProject} data-testid="create-invoice-from-project">
                  <Receipt size={16} /> Creer une facture
                </button>
                <button className="btn btn-secondary" onClick={() => setShowPhotoModal(true)}>
                  <Camera size={16} /> Ajouter photo
                </button>
                <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/pdf-reports/projects/${selectedProject.id}`, '_blank')} data-testid="project-report-pdf">
                  <FilePdf size={16} /> Compte rendu PDF
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  setEditProject(selectedProject);
                  setProjectForm({
                    name: selectedProject.name,
                    client_name: selectedProject.client_name || '',
                    client_id: selectedProject.client_id || '',
                    address: selectedProject.address || '',
                    sale_price_ht: selectedProject.sale_price_ht,
                    tva_rate: selectedProject.tva_rate,
                    hourly_rate: selectedProject.hourly_rate || 0,
                    start_date: selectedProject.start_date || '',
                    end_date: selectedProject.end_date || '',
                    autoliquidation_tva: !!selectedProject.autoliquidation_tva,
                    guarantee_type: selectedProject.guarantee_type || null,
                    retention_percent: selectedProject.retention_percent || null,
                    retention_release_date: selectedProject.retention_release_date || '',
                    caution_attestation_doc_id: selectedProject.caution_attestation_doc_id || '',
                    prorata_enabled: !!selectedProject.prorata_enabled,
                    prorata_percent: selectedProject.prorata_percent || 0,
                    prorata_amount: selectedProject.prorata_amount || 0,
                    contract_type: selectedProject.contract_type || 'direct',
                    purchase_order_number: selectedProject.purchase_order_number || '',
                    purchase_order_doc_id: selectedProject.purchase_order_doc_id || '',
                    mandataire_company: selectedProject.mandataire_company || '',
                  });
                  setShowProjectModal(true);
                }}>
                  Modifier
                </button>
                <button className="btn btn-secondary" onClick={() => handleGenerateDGD(selectedProject.id)} data-testid="generate-dgd-btn" style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#F59E0B' }}>
                  <FileText size={16} /> Generer DGD
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  if (!window.confirm("Envoyer le DGD + la facture de solde au client par email ?\nIl faut qu'un DGD ait deja ete genere (cherche la facture de solde la plus recente).")) return;
                  handleSendDGD(selectedProject.id);
                }} data-testid="send-dgd-btn" style={{ background: '#DBEAFE', color: '#1E40AF', borderColor: '#3B82F6' }}>
                  <PaperPlaneTilt size={16} /> Envoyer DGD + Facture client
                </button>
                <button className="btn btn-secondary" onClick={handleArchiveProject} data-testid="archive-project-btn">
                  <ArchiveIcon size={16} /> Archiver
                </button>
                <button className="btn btn-danger" onClick={() => handleDeleteProject(selectedProject.id)}>
                  <Trash size={16} />
                </button>
              </div>

              {/* Tabs (drag & drop reorder) */}
              <ProjectTabsBar tabs={[
                { key: 'feed', label: 'Fil d\'avancement' },
                { key: 'gestion', label: 'Gestion' },
                { key: 'commercial', label: `Devis & Situations (${projectQuotes.length}/${billingInvoices.length})` },
                { key: 'subcontractors', label: 'Sous-traitants' },
                { key: 'contacts', label: 'Contacts chantier' },
                { key: 'photos', label: `Photos (${photos.length})` },
                { key: 'documents', label: 'Documents' },
                { key: 'achats', label: 'Achats' },
                { key: 'labor_monthly', label: 'Main-d\'oeuvre' },
                { key: 'reservations', label: 'SAV / Reserves' }
              ]} activeTab={activeTab} setActiveTab={setActiveTab} />

              {activeTab === 'feed' && <WorksiteFeedTab project={selectedProject} currentUser={currentUser} />}
              {activeTab === 'commercial' && <ProjectQuotesPanel project={selectedProject} billingInvoices={billingInvoices} onProjectFinanceChange={async () => {
                try {
                  const res = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
                  const fresh = (res.data || []).find(p => p.id === selectedProject.id);
                  if (fresh) setSelectedProject(fresh);
                } catch (e) { /* noop */ }
                fetchProjectDetails(selectedProject.id);
              }} />}
              {activeTab === 'subcontractors' && <ProjectSubcontractorsTab project={selectedProject} />}
              {activeTab === 'contacts' && <ProjectContactsTab project={selectedProject} currentUser={currentUser} />}
              {activeTab === 'photos' && <PhotosTabGrouped photos={photos} project={selectedProject} onUpload={() => setShowPhotoModal(true)} onUpdateComment={handleUpdatePhotoComment} onDelete={handleDeletePhoto} editingPhotoId={editingPhotoId} setEditingPhotoId={setEditingPhotoId} editPhotoComment={editPhotoComment} setEditPhotoComment={setEditPhotoComment} setPreviewPhoto={setPreviewPhoto} />}
              {activeTab === 'documents' && <ProjectDocumentsViewer project={selectedProject} canManage={true} />}
              {activeTab === 'achats' && <ProjectPurchasesTab project={selectedProject} isAdmin={true} />}
              {activeTab === 'labor_monthly' && <ProjectLaborMonthlyTab project={selectedProject} />}
              {activeTab === 'gestion' && <BudgetTab project={selectedProject} />}
              {activeTab === 'reservations' && <ReservationsTab project={selectedProject} currentUser={currentUser} />}
        </div>
      ) : (
        <>
          {/* Contract type filter chips + Layout toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }} data-testid="projects-filter-bar">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CONTRACT_TYPES.map(ct => {
                const active = contractFilter === ct.value;
                const count = ct.value === 'all'
                  ? projects.length
                  : projects.filter(p => (p.contract_type || 'direct') === ct.value).length;
                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => setContractFilter(ct.value)}
                    data-testid={`contract-filter-${ct.value}`}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1px solid ${active ? ct.color : '#E2E2E6'}`,
                      background: active ? ct.color : 'white',
                      color: active ? 'white' : '#374151',
                      fontSize: '0.82rem',
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {ct.label} <span style={{ opacity: 0.7 }}>({count})</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.78rem', color: '#5E5F66' }}>
              <span>Affichage :</span>
              <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="projects-layout" />
            </div>
          </div>
          <ProjectsGrid projects={filteredProjects} layoutMode={layoutMode} onOpen={(p) => { setSelectedProject(p); setActiveTab('gestion'); }} onUploadCover={handleCoverUpload} onDeleteCover={handleCoverDelete} canManage={true} />
        </>
      )}

      <ProjectFormModal show={showProjectModal} onClose={() => setShowProjectModal(false)} onSubmit={handleCreateProject} form={projectForm} setForm={setProjectForm} editProject={editProject} />
      <ExpenseFormModal show={showExpenseModal} onClose={() => setShowExpenseModal(false)} onSubmit={handleCreateExpense} form={expenseForm} setForm={setExpenseForm} categories={EXPENSE_CATEGORIES} />
      <PhotoUploadModal show={showPhotoModal} onClose={() => setShowPhotoModal(false)} onSubmit={handleUploadPhoto} photoFile={photoFile} setPhotoFile={setPhotoFile} photoComment={photoComment} setPhotoComment={setPhotoComment} photoFolder={photoFolder} setPhotoFolder={setPhotoFolder} />
      <PhotoPreviewModal photo={previewPhoto} onClose={() => setPreviewPhoto(null)} apiUrl={API_URL} />
      <QuickInvoiceModal show={quickInvoice.show} type={quickInvoice.type} projectId={selectedProject?.id} onClose={() => setQuickInvoice({ show: false, type: 'facture' })} onCreated={() => selectedProject && fetchProjectDetails(selectedProject.id)} />
      <QuoteSelectorModal
        show={showProjectQuoteSelector}
        projectId={selectedProject?.id}
        onClose={() => setShowProjectQuoteSelector(false)}
        onPickQuote={handlePickQuoteForProject}
        onCreateNewQuote={handleCreateNewQuoteFromProject}
      />
      {convertDialog.open && convertDialog.quote && (
        <QuotePercentageDialog
          quote={convertDialog.quote}
          previousAdvancements={convertDialog.previousAdvancements}
          onClose={() => setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} })}
          onConfirm={handleConvertConfirmProject}
          submitting={convertDialog.submitting}
        />
      )}    </div>
  );
};

export default ProjectsPage;
